import { useEffect, useMemo, useState } from 'react';
import { AvisoMetas } from '@/components/AvisoCarga';
import BotaoCopiar from '@/components/relatorios/BotaoCopiar';
import { chaveMes } from '@/metas/calculo';
import { CAMPOS_GERAIS, ouvirMetas, salvarMetasGerais } from '@/services/metasService';
import { METAS_GRADE_PADRAO, montarGradeDiaria } from '@/metas/gradeDiaria';
import { expedienteDaClinica } from '@/produtividade/expediente';
import { feriadosDaClinica } from '@/produtividade/feriados';
/**
 * ★★★ Os formatadores vêm do MÓDULO DO TEXTO, não de cópias locais — é o que garante que a
 * célula da tela e a linha copiada imprimam o mesmo número. Ver o cabeçalho de `gradeDia.js`.
 */
import {
  diaPadraoDaGrade, formatarCelula, inteiro, moedaCurta, pct1, textoDoDiaDaGrade,
} from '@/relatorios/gradeDia';

/**
 * ★★★ A-7a — A GRADE DIÁRIA DA CLÍNICA, no FINAL do Dashboard.
 *
 * ★★★ A-7c (13/out/2026) — ela deixou de ser "só do gestor": **quem entra no Dashboard vê**
 * (gestor, SDR e negociador), e quem não é gestor vê em **modo leitura** (`somenteLeitura`) —
 * meta como texto, sem caixinha e sem "Salvar metas". ⚠ Recepção não entra na rota.
 * ★ Ganhou também **"Copiar resultado do dia"**, do dia que a pessoa clicar no cabeçalho
 * (padrão: o último dia já acontecido).
 *
 * ★ Só desenha: toda a conta vem de `montarGradeDiaria` (puro, testado sem navegador). A
 * tela não decide nenhuma régua — se precisar decidir, o lugar é o módulo.
 *
 * ★★★ O QUE ELA SUBSTITUI: o diálogo "Configurar metas gerais". ⚠ A meta é digitada **na
 * própria coluna "Meta"**, como na planilha do dono, e "Salvar metas" grava **só os campos
 * gerais** — `metasPorSdr` e `diasUteis` não são tocados (ver `metasService`).
 *
 * ★★ O SELETOR DE MÊS É PRÓPRIO, e o rótulo diz isso por extenso: *"independente do filtro
 * de período acima"*. ⚠ Sem essa frase, o gestor filtra "Meta Ads" no topo e acha que a
 * grade embaixo quebrou — ela **não** aplica escopo nem filtro de origem.
 */

const moeda = moedaCurta;
const porcento = pct1;
const formatar = formatarCelula;
const SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

/**
 * `'2026-10-13'` → `'ter 13/10'`.
 *
 * ⚠⚠ O dia da semana sai de `grade.dias`, e a data é recortada da STRING — nunca de
 * `new Date('2026-10-01')`, que é meia-noite **UTC** e em São Paulo imprime **30/09**. ★ É o
 * defeito medido que `relatorios/formato.js` registra em `diaCurto`.
 */
function rotuloDoDia(grade, chave) {
  const d = (grade?.dias ?? []).find((x) => x.chave === chave);
  const dm = `${chave.slice(8)}/${chave.slice(5, 7)}`;
  return d ? `${SEMANA[d.diaSemana]} ${dm}` : dm;
}

/** O quadradinho da célula: verde atingiu, vermelho abaixo, cinza sem meta, vazio no futuro. */
function Celula({ celula, linha, futuro }) {
  if (futuro) return <span className="gd-b gd-vazio">·</span>;
  /**
   * ★ O número do quadrinho é a QUANTIDADE (o desenho aprovado).
   * ⚠⚠ A-7c: nas duas linhas de dinheiro ele passou a ser em **milhares** (`18,5`), e o valor
   * INTEIRO foi para o `title` — sem isso o número curto esconderia dado.
   */
  const texto = formatar(celula.valor, linha.formatoCelula ?? linha.formato);
  const classe = celula.atingiu === null ? 'gd-neutro' : celula.atingiu ? 'gd-ok' : 'gd-nao';
  const partes = [];
  if (linha.formatoCelula === 'milhares' && celula.valor !== null && celula.valor !== undefined) {
    partes.push(moeda(celula.valor));
  }
  if (celula.taxa !== null && celula.taxa !== undefined) partes.push(`${porcento(celula.taxa)} no dia`);
  return (
    <span className={`gd-b ${classe}`} title={partes.length ? partes.join(' · ') : undefined}>
      {texto}
    </span>
  );
}

/**
 * @param somenteLeitura ★★★ A-7c — quem NÃO é gestor vê a grade inteira, com a meta como
 *   TEXTO: ⚠ **sem caixinha e sem "Salvar metas"**, e não "desabilitado". ★ Botão desabilitado
 *   continua anunciando que existe algo ali que a pessoa não pode — e as rules recusariam a
 *   escrita de qualquer forma (`allow update: if isGestor && camposDeMetas()`).
 */
export default function GradeDiaria({
  clinicaId, leads = [], agendamentos = [], agora = new Date(), somenteLeitura = false,
  configDaClinica = null,
}) {
  const [mes, setMes] = useState(() => chaveMes(agora));
  const [metas, setMetas] = useState(null);
  const [falhaMetas, setFalhaMetas] = useState(false);
  /** O que está nos campos da coluna "Meta" — TEXTO, até salvar. */
  const [rascunho, setRascunho] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);
  const [salvo, setSalvo] = useState(false);
  /**
   * ★★ A-7c — o dia que "Copiar resultado do dia" copia. `null` = **o padrão**, que é o último
   * dia já acontecido (`diaPadraoDaGrade`). ⚠ Guardar `null` em vez do dia calculado é o que
   * faz o padrão acompanhar a troca de mês sem nenhum efeito.
   */
  const [diaEscolhido, setDiaEscolhido] = useState(null);

  useEffect(() => {
    if (!clinicaId) return undefined;
    setFalhaMetas(false);
    return ouvirMetas(clinicaId, mes, setMetas, () => setFalhaMetas(true));
  }, [clinicaId, mes]);

  // ★ O rascunho nasce do documento do mês a cada troca de mês: digitar num mês e navegar
  //   para outro não pode levar o número junto.
  useEffect(() => {
    const base = { ...METAS_GRADE_PADRAO, ...metas };
    setRascunho(Object.fromEntries(CAMPOS_GERAIS.map((c) => [c, base[c]])));
    setSalvo(false);
    setErro(null);
  }, [metas, mes]);

  /**
   * ⚠ A grade usa o RASCUNHO, não o documento: o desenho aprovado diz *"troque um número na
   * coluna Meta e aperte Tab"* — o funil invertido e as cores mudam na hora, antes de salvar.
   */
  const metasDaGrade = useMemo(() => ({ ...metas, ...rascunho }), [metas, rascunho]);
  /**
   * ★★ 02/11/2026 — os MESMOS feriados e o MESMO expediente dos Indicadores (D7): o feriado
   * não vira coluna e sai da divisão da meta de valor; os dias úteis saem do expediente da
   * clínica, e não mais de `metas.diasUteis` (ver `montarGradeDiaria`).
   */
  const feriados = useMemo(() => feriadosDaClinica(configDaClinica), [configDaClinica]);
  const expediente = useMemo(() => expedienteDaClinica(configDaClinica), [configDaClinica]);
  const grade = useMemo(
    () => montarGradeDiaria({ mes, leads, agendamentos, metas: metasDaGrade, feriados, expediente, agora }),
    [mes, leads, agendamentos, metasDaGrade, feriados, expediente, agora],
  );

  /** ★ O dia em foco: o escolhido, se ainda existir neste mês; senão o padrão. */
  const diaDoTexto = useMemo(() => {
    const existe = grade.dias.some((d) => d.chave === diaEscolhido && !d.futuro);
    return existe ? diaEscolhido : diaPadraoDaGrade(grade);
  }, [grade, diaEscolhido]);

  function mudarMes(passo) {
    const [ano, m] = mes.split('-').map(Number);
    setMes(chaveMes(new Date(ano, m - 1 + passo, 1)));
    // ⚠ Trocar de mês solta a escolha: o dia 7 de outubro não é o dia 7 de setembro.
    setDiaEscolhido(null);
  }

  async function salvar() {
    setErro(null);
    setSalvando(true);
    try {
      await salvarMetasGerais(clinicaId, mes, rascunho);
      setSalvo(true);
    } catch (e) {
      setErro(e.code === 'permission-denied'
        ? 'Apenas o gestor da clínica pode definir metas.'
        : (e.message ?? 'Não foi possível salvar as metas.'));
    } finally {
      setSalvando(false);
    }
  }

  const rotuloMes = new Date(`${mes}-02T12:00:00`)
    .toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  const { funil } = grade;
  const semFunil = funil.leadsDia === null;

  /**
   * A célula da coluna "Meta".
   *
   * ★★★ A-7c — em `somenteLeitura` ela é **texto**, e o `<input>` não existe no DOM. ⚠ Não é
   * `readOnly` nem `disabled`: o pedido do dono é *"sem caixinha, sem Salvar metas"*.
   */
  const campo = (chave, sufixo) => {
    if (somenteLeitura) {
      const bruto = rascunho[chave];
      const n = Number(String(bruto ?? '').replace(',', '.'));
      const texto = Number.isFinite(n) && n > 0
        ? (sufixo === 'R$' ? moeda(n) : `${inteiro(n)}%`)
        : '—';
      return <span className="gd-meta-texto">{texto}</span>;
    }
    return (
      <span className="gd-meta-in">
        <input
          inputMode="decimal"
          aria-label={`Meta de ${chave}`}
          value={rascunho[chave] ?? ''}
          onChange={(e) => setRascunho((r) => ({ ...r, [chave]: e.target.value }))}
        />
        {sufixo && <small>{sufixo}</small>}
      </span>
    );
  };

  return (
    <section className="cartao grade-diaria" id="grade-diaria">
      {falhaMetas && <AvisoMetas />}
      <div className="gd-cabecalho">
        <div>
          <h2 className="painel-titulo">Grade diária da clínica</h2>
          <div className="gd-rotulo">
            {rotuloMes} · <strong>{grade.totalDiasUteis} dias úteis</strong> ·{' '}
            {/* ⚠ A frase do desenho aprovado, ao pé da letra: ela é o que impede ler a grade
                como se ela seguisse os filtros do topo. */}
            independente do filtro de período acima
          </div>
        </div>
        <div className="gd-acoes">
          <button type="button" className="btn-secundario" aria-label="Mês anterior" onClick={() => mudarMes(-1)}>‹</button>
          <input
            type="month"
            aria-label="Mês da grade"
            value={mes}
            onChange={(e) => { setMes(e.target.value || mes); setDiaEscolhido(null); }}
          />
          <button type="button" className="btn-secundario" aria-label="Próximo mês" onClick={() => mudarMes(1)}>›</button>
          {/* ⚠⚠ O botão NÃO é renderizado em modo leitura — ver `campo` acima. */}
          {!somenteLeitura && (
            <button type="button" className="btn-primario compacto" onClick={salvar} disabled={salvando}>
              {salvando ? 'Salvando…' : 'Salvar metas'}
            </button>
          )}
        </div>
      </div>

      {erro && <p className="erro" role="alert">{erro}</p>}
      {salvo && !erro && <p className="gd-salvo" role="status">Metas salvas.</p>}

      {grade.semDiasUteis ? (
        <p className="campo-ajuda">
          Nenhum dia útil configurado para este mês — a grade não tem colunas. Marque os dias
          em que a clínica atende para a meta diária voltar a ter divisor.
        </p>
      ) : (
        <>
          {/* ⚠ Só ESTA caixa rola para o lado; a página não. É o que o desenho pede e o que o
              teste de navegador a 1366×768 afirma. */}
          <div className="gd-caixa">
            <table className="gd-tabela">
              <thead>
                <tr>
                  <th className="gd-fixa gd-c1">Indicador</th>
                  <th className="gd-fixa gd-c2">Meta</th>
                  <th className="gd-fixa gd-c3">Total</th>
                  <th className="gd-fixa gd-c4">Média diária</th>
                  {grade.dias.map((d) => {
                    const rotulo = (
                      <>
                        Dia {Number(d.chave.slice(8))}
                        <small>{SEMANA[d.diaSemana]}</small>
                      </>
                    );
                    const classe = `gd-dia${d.hoje ? ' gd-hoje' : ''}`
                      + `${d.chave === diaDoTexto ? ' gd-sel' : ''}`;
                    /**
                     * ★★★ A-7c — O CABEÇALHO DO DIA É UM `<button>` DE VERDADE, e clicar nele
                     * escolhe o dia que o "Copiar resultado do dia" copia.
                     * ⚠ Dia FUTURO não vira botão: não há resultado a copiar, e um botão que
                     * não faz nada é pior que nenhum.
                     */
                    return (
                      <th key={d.chave} className={classe}>
                        {d.futuro ? rotulo : (
                          <button
                            type="button"
                            className="gd-dia-botao"
                            aria-pressed={d.chave === diaDoTexto}
                            title="Escolher este dia para copiar o resultado"
                            onClick={() => setDiaEscolhido(d.chave)}
                          >
                            {rotulo}
                          </button>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {grade.linhas.map((linha) => (
                  <tr key={linha.id} data-linha={linha.id}>
                    <td className="gd-fixa gd-c1">
                      {linha.rotulo}
                      <small>{linha.nota}</small>
                    </td>
                    <td className="gd-fixa gd-c2">
                      {linha.meta.tipo === 'auto' ? (
                        <span className="gd-meta-auto">
                          {linha.meta.valor === null ? '—' : <strong>{linha.meta.valor}</strong>}
                          <small>
                            {linha.meta.valor === null ? 'sem meta' : `${funil.leadsMes}/mês · automática`}
                          </small>
                        </span>
                      ) : linha.meta.tipo === 'pct' ? (
                        campo(linha.meta.campo, '%')
                      ) : (
                        campo(linha.meta.campo, 'R$')
                      )}
                    </td>
                    {/* ⚠ A-7c: o `title` carrega o valor inteiro — a coluna tem 104px e corta
                        com "…" em 7 dígitos (ver `.gd-c3` no CSS). */}
                    <td
                      className="gd-fixa gd-c3"
                      title={linha.totalTaxa === undefined
                        ? formatar(linha.total, linha.formato)
                        : `${formatar(linha.totalTaxa, 'pct')} (${inteiro(linha.total)})`}
                    >
                      {linha.totalTaxa === undefined
                        ? formatar(linha.total, linha.formato)
                        : `${formatar(linha.totalTaxa, 'pct')} (${inteiro(linha.total)})`}
                    </td>
                    <td className="gd-fixa gd-c4">{formatar(linha.media, linha.formato)}</td>
                    {linha.celulas.map((c, i) => (
                      <td key={c.chave} className={`gd-dia${grade.dias[i].hoje ? ' gd-hoje' : ''}`}>
                        <Celula celula={c} linha={linha} futuro={grade.dias[i].futuro} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="gd-legenda">
            <span><span className="gd-b gd-ok">8</span> atingiu a meta do dia</span>
            <span><span className="gd-b gd-nao">3</span> abaixo da meta</span>
            <span><span className="gd-b gd-neutro">—</span> sem meta ou sem denominador</span>
            <span><span className="gd-b gd-vazio">·</span> dia que ainda não chegou</span>
            <span>Moldura azul = hoje</span>
            <span>Sublinhado escuro = dia escolhido para copiar</span>
          </div>

          {/**
            * ★★★ A-7c — "COPIAR RESULTADO DO DIA", para colar no grupo do WhatsApp.
            *
            * ★ Aparece para **todo mundo que vê a grade** — gestor, SDR e negociador (decisão
            * do dono): é o resultado DA CLÍNICA, não o de uma pessoa.
            * ⚠ Reusa `BotaoCopiar` inteiro, que já resolve as duas saídas honestas (copiou /
            * `textarea` já selecionado quando o navegador recusa) e o "ver texto".
            * ⚠⚠ O texto sai de `textoDoDiaDaGrade(grade, …)`, que **lê a célula** — nenhuma
            * conta nova. É o que faz o texto não poder divergir da coluna.
            */}
          {diaDoTexto && (
            <div className="gd-copiar">
              <span className="rotulo" id="gd-copiar-rotulo">
                Dia escolhido: <strong>{rotuloDoDia(grade, diaDoTexto)}</strong> — clique no cabeçalho
                de outro dia para trocar.
              </span>
              <BotaoCopiar
                rotulo={`Copiar resultado de ${rotuloDoDia(grade, diaDoTexto)}`}
                montarTexto={() => textoDoDiaDaGrade(grade, diaDoTexto)}
              />
            </div>
          )}

          <div className="gd-funil">
            <h3>Funil invertido — de onde sai a meta de leads</h3>
            {semFunil ? (
              <p className="campo-ajuda gd-sem-funil">
                digite a meta de vendas e o ticket médio para calcular
              </p>
            ) : (
              <div className="gd-funil-passos">
                <span className="gd-passo"><small>Meta de vendas</small><b>{moeda(grade.config.metaValorVendas)}</b></span>
                <span className="gd-op">÷ ticket médio {moeda(grade.config.ticketMedioAlvo)}</span>
                <span className="gd-passo"><small>Fechamentos</small><b>{inteiro(funil.fechamentos)}</b></span>
                <span className="gd-op">÷ fechamento {inteiro(grade.config.metaFechamentoPct)}%</span>
                <span className="gd-passo"><small>Comparecimentos</small><b>{inteiro(funil.comparecimentos)}</b></span>
                <span className="gd-op">÷ comparecimento {inteiro(grade.config.metaComparecimentoPct)}%</span>
                <span className="gd-passo"><small>Agendamentos</small><b>{inteiro(funil.agendamentos)}</b></span>
                <span className="gd-op">÷ agendamento {inteiro(grade.config.metaAgendamentoPct)}%</span>
                <span className="gd-passo gd-fim"><small>Leads necessários no mês</small><b>{funil.leadsMes}</b></span>
                <span className="gd-op">÷ {grade.totalDiasUteis} dias úteis</span>
                <span className="gd-passo gd-fim"><small>Meta de leads por dia</small><b>{funil.leadsDia}</b></span>
              </div>
            )}
          </div>

          <p className="gd-nota">
            Sábado, domingo e feriado não viram coluna: o que acontece neles soma no{' '}
            <strong>dia útil seguinte</strong> (e, no fim do mês, no último dia útil) — por
            isso a soma das colunas é sempre o Total.{' '}
            <strong>Comparecimento é a data da BAIXA</strong>, a mesma do funil e da folha:
            mais da metade das baixas é de consulta de outro dia. A taxa do dia pode passar de
            100 %.
          </p>
        </>
      )}
    </section>
  );
}
